/**
 * REST client for the projects API (§7g).
 *
 * All endpoints require JWT auth — the shared request() helper pulls
 * the Cognito ID token from auth.js and appends it as a Bearer header.
 * Returns parsed JSON, or null for 204 No Content.
 */

import { request } from "./api.js";

export const listProjects = ({ includeShared = false } = {}) => {
  const qs = includeShared ? "?includeShared=true" : "";
  return request(`/api/projects${qs}`);
};

export const getProject = (id) => request(`/api/projects/${id}`);

// ── v4.1 Phase 36.4 — member CRUD + ownership transfer + invites ───────────
// All gated server-side by COLLAB_MODE; the frontend additionally reads
// VITE_COLLAB_MODE (see src/config.js) and hides affordances when off.

export const addProjectMember = (projectId, { userId, email, role }) => {
  const body = email ? { email, role } : { userId, role };
  return request(`/api/projects/${projectId}/members`, {
    method: "POST",
    body: JSON.stringify(body),
  });
};

export const removeProjectMember = (projectId, userId) =>
  request(
    `/api/projects/${projectId}/members/${encodeURIComponent(userId)}`,
    { method: "DELETE" },
  );

export const updateProjectMember = (projectId, userId, { role }) =>
  request(`/api/projects/${projectId}/members/${encodeURIComponent(userId)}`, {
    method: "PATCH",
    body: JSON.stringify({ role }),
  });

export const transferProjectOwnership = (projectId, newOwnerId) =>
  request(`/api/projects/${projectId}/members/transfer-ownership`, {
    method: "POST",
    body: JSON.stringify({ newOwnerId }),
  });

export const revokeProjectInvite = (projectId, email) =>
  request(
    `/api/projects/${projectId}/invites/${encodeURIComponent(email)}`,
    { method: "DELETE" },
  );

// ── v4.1 Phase 37.3 — share-link CRUD + resolver ─────────────────────────

export const createShareToken = (projectId, { expiresInDays } = {}) =>
  request(`/api/projects/${projectId}/shares`, {
    method: "POST",
    body: JSON.stringify(
      expiresInDays !== undefined ? { expiresInDays } : {},
    ),
  });

export const listShareTokens = (projectId) =>
  request(`/api/projects/${projectId}/shares`);

export const resolveShareToken = (token) =>
  request(`/api/shares/${encodeURIComponent(token)}`);

export const revokeShareToken = (token) =>
  request(`/api/shares/${encodeURIComponent(token)}`, { method: "DELETE" });

export const createProject = ({ name, description, companyUrl }) =>
  request("/api/projects", {
    method: "POST",
    body: JSON.stringify({ name, description, companyUrl }),
  });

export const updateProject = (id, patch) =>
  request(`/api/projects/${id}`, {
    method: "PUT",
    body: JSON.stringify(patch),
  });

export const archiveProject = (id) =>
  request(`/api/projects/${id}`, { method: "DELETE" });
